import { useCallback, useEffect, useRef, useState } from 'react';

export const setBrowserStreamInputEnabled = (
  frame: HTMLIFrameElement,
  enabled: boolean
) => {
  // Chromium can clear cross-origin iframe focus as soon as inert is applied
  const wasFocused = document.activeElement === frame;
  frame.inert = !enabled;
  frame.tabIndex = enabled ? 0 : -1;
  frame.style.pointerEvents = enabled ? '' : 'none';
  if (!enabled && wasFocused) {
    frame.contentWindow?.blur();
    frame.blur();
    frame.parentElement?.focus({ preventScroll: true });
    // Cross-origin blur releases keys, but cannot release held mouse buttons
    frame.setAttribute('src', frame.src);
  }
};

/**
 * Local takeover for an always-interactive stream: the iframe accepts input
 * only after Take control, while `active` holds and the document is visible
 */
export const useBrowserStreamInput = (active: boolean) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isInputEnabled, setIsInputEnabled] = useState(false);
  // Losing the view ends takeover, so a later resume starts without input
  if (isInputEnabled && !active) setIsInputEnabled(false);

  const takeControl = useCallback(() => {
    if (document.visibilityState !== 'hidden') setIsInputEnabled(true);
  }, []);

  const returnControl = useCallback(() => {
    const frame = iframeRef.current;
    // Close the input gate now, before React commits the re-render
    if (frame) setBrowserStreamInputEnabled(frame, false);
    setIsInputEnabled(false);
  }, []);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') returnControl();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [returnControl]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onBlur = () => {
      // Embedded viewers can focus themselves after connecting, even when inert
      clearTimeout(timer);
      timer = setTimeout(() => {
        const frame = iframeRef.current;
        if (frame?.inert && document.activeElement === frame) {
          frame.contentWindow?.blur();
          frame.parentElement?.focus({ preventScroll: true });
        }
      }, 0);
    };
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('blur', onBlur);
      clearTimeout(timer);
    };
  }, []);

  return {
    iframeRef,
    canInteract: isInputEnabled && active,
    takeControl,
    returnControl,
  };
};
